// WebSocketContext.tsx
import React, { useContext, useEffect, useMemo, useState } from 'react';

import config from '../../config/config';
import getFirstSubdomain, { getValidDomain } from '../../shared-functions/DomiansplitFunction';
import decodeJWT from '../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem, setLocalStorageItem } from '../../shared-functions/LocalStorage';
import charger from '../../utils/api/charger';
import webSocketService from '../../websocket/wsService';
import { useBranding } from '../BrandingContext';

import WebSocketContext from './WebSocketContext';

/**
 * Web socket provider
 * @param param0
 * @returns
 */
export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const [guestToken, setGuestToken] = useState(getLocalStorageItem('guestToken'));
  const domain = getFirstSubdomain();
  const validDomain = getValidDomain();
  const { brandData } = useBranding();

  /**
   * Function to generate Token
   */
  const generateTemporaryToken = async () => {
    try {
      const response = await charger.generateTempToken();
      setGuestToken(response?.data?.guestToken);
      setLocalStorageItem('guestToken', response?.data?.guestToken);
    } catch (error: any) {
      console.error(error);
    }
  };

  /**
   * Function to reconnect WebSocket
   * @returns
   */
  const reconnectWebSocket = async () => {
    try {
      // If a connection already exists, close it before reconnecting
      webSocketService.disconnect();

      const latestToken = getLocalStorageItem('guestToken');
      if (!latestToken) {
        return; // never reconnect if no token
      }

      const decodedToken = decodeJWT(latestToken);
      if (!decodedToken?.transactionId) {
        console.error('Invalid transaction ID');
        return;
      }

      const socketUrl = `${domain && brandData?.webSocketUrl ? brandData?.webSocketUrl : config.socketUrl}/end-consumer?mode=guest&transactionId=${decodedToken.transactionId}`;
      webSocketService.connect(socketUrl, validDomain ? [validDomain] : undefined);
    } catch (error) {
      console.error('WebSocket reconnection failed:', error);
    }
  };

  useEffect(() => {
    if (guestToken) {
      const decodedToken = decodeJWT(guestToken || '');
      if (!decodedToken?.transactionId) {
        console.error('Invalid transaction ID'); // Handle invalid transactionId
      }

      // Connect to WebSocket
      const socketUrl = `${domain && brandData?.webSocketUrl ? brandData?.webSocketUrl : config.socketUrl}/end-consumer?mode=guest&transactionId=${decodedToken?.transactionId}`;
      webSocketService.connect(socketUrl, validDomain ? [validDomain] : undefined);

      return () => {
        // Disconnect WebSocket on cleanup
        webSocketService.disconnect();
      };
    }

    return undefined;
  }, [guestToken]);

  // Use useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      webSocketService,
      generateTemporaryToken,
      reconnectWebSocket,
    }),
    [webSocketService, generateTemporaryToken, reconnectWebSocket],
  );

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}

/**
 * Use websocket context
 * @returns
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    console.warn('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
